import { AlertType, Button, Cell, FormControl, Grid, Heading, HFlow, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Box } from 'components/Box'
import { SubmitButton, TextField } from 'components/form'
import { usePecField } from 'components/form/final-form/hooks/useField'
import { getFieldError } from 'components/form/final-form/util'
import { useIsDiretorioAnexoArquivosValidoLazyQuery } from 'graphql/hooks.generated'
import React, { useEffect } from 'react'
import { metaPath } from 'util/metaPath'

import { BOTAO_TESTAR_TOOLTIP_TEXT, ConfiguracaoAnexoArquivosModel, VALIDATION_ERROR_MSG } from '../model-anexoArquivos'

interface HabilitarAnexoArquivosFormProps {
  habilitado: boolean
  diretorio: string
  hasServerSideError: boolean
  setHasServerSideError: (err: boolean) => void
  handleSubmit: (event?: React.SyntheticEvent<HTMLFormElement>) => void
}

const name = metaPath<ConfiguracaoAnexoArquivosModel>()

export function HabilitarAnexoArquivosForm(props: HabilitarAnexoArquivosFormProps) {
  const { habilitado, diretorio, handleSubmit, hasServerSideError, setHasServerSideError } = props
  const {
    meta,
    input: { value: diretorioFormValue },
  } = usePecField({
    name: name.diretorio,
  })
  const alert = useAlert()
  const [
    anexoArquivoDirectoryTest,
    { data: directoryTestData, loading: directoryTestLoading },
  ] = useIsDiretorioAnexoArquivosValidoLazyQuery()
  const handleTestClick = (dir: string) => {
    anexoArquivoDirectoryTest({ variables: { diretorio: dir.trim() } })
  }

  useEffect(() => {
    if (directoryTestData) {
      const alertData: { type: AlertType; message: React.ReactNode } = directoryTestData.isDiretorioAnexoArquivosValido
        ? {
            type: 'success',
            message: 'Verificação realizada com sucesso. O diretório informado foi encontrado e está acessível.',
          }
        : { type: 'danger', message: VALIDATION_ERROR_MSG }

      setHasServerSideError(!directoryTestData.isDiretorioAnexoArquivosValido)
      alert(alertData.type, alertData.message)
    }
  }, [alert, directoryTestData, setHasServerSideError])

  return (
    <Box>
      <VFlow>
        <HFlow hSpacing={0.5}>
          <Heading level={3}>Habilitar anexo de arquivos</Heading>
          {habilitado ? (
            <Tag type='success' icon='checkCircleFilled'>
              Habilitado
            </Tag>
          ) : (
            <Tag type='normal' icon='banOutline'>
              Desabilitado
            </Tag>
          )}
        </HFlow>
        <Text>
          Ao habilitar o anexo de arquivos, os profissionais de saúde poderão anexar arquivos ao prontuário de um
          cidadão caso necessário. Esses arquivos serão armazenados no diretório informado abaixo e podem ser
          visualizados no prontuário do cidadão por todos os profissionais que tiverem acesso. Imagens e documentos de
          saúde podem ocupar um espaço considerável de armazenamento e é necessário um monitoramento para garantir que a
          infraestrutura está suportando a demanda.
        </Text>

        <Grid gap={0.5}>
          <Cell size={3}>
            <TextField
              label='Diretório de destino dos arquivos'
              name={name.diretorio}
              disabled={!!diretorio}
              maxLength={255}
              error={hasServerSideError ? VALIDATION_ERROR_MSG : getFieldError(meta)}
              required
            />
          </Cell>
          {!diretorio && (
            <Cell>
              <FormControl label={<span>&nbsp;</span>}>
                <Tooltip text={BOTAO_TESTAR_TOOLTIP_TEXT}>
                  <Button
                    kind='normal'
                    size='small'
                    onClick={() => handleTestClick(diretorioFormValue)}
                    loading={directoryTestLoading}
                    disabled={!diretorioFormValue?.trim()}
                  >
                    Testar
                  </Button>
                </Tooltip>
              </FormControl>
            </Cell>
          )}
        </Grid>
        <SubmitButton kind={habilitado ? 'normal' : 'primary'} size='small' handleSubmit={handleSubmit}>
          {habilitado ? 'Desabilitar' : 'Habilitar'}
        </SubmitButton>
      </VFlow>
    </Box>
  )
}
